'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { Service } from '../../types/services';

import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

import { RxDoubleArrowDown } from 'react-icons/rx';
import { Button } from '../ui/button';

import { LuPlus } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ServiceHeader({
  service,
  locale,
  city,
}: {
  service: Service;
  locale: string;
  city?: string;
}) {
  const { t } = useTranslations();

  const tags = [t('text3593'), t('text3594'), t('text3595')];

  const isTablet = useBreakpoint('sm');
  const isDesktop = useBreakpoint('lg');

  const handleScrollDown = () => {
    const scrollHeight = isDesktop ? 720 : isTablet ? 620 : 500;
    window.scrollBy({ top: scrollHeight, behavior: 'smooth' });
  };

  const id = locale === 'sk' ? 'kontaktny-formular' : 'kontaktformular';

  return (
    <section
      className={cn(
        'custom-section',
        'relative h-[400px] items-center justify-end pb-16 sm:h-[500px] sm:pb-[72px] lg:h-[600px]',
      )}
    >
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-start justify-start gap-8 sm:gap-10 md:flex-row md:items-end md:justify-between lg:gap-12',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-12 md:max-w-[690px]">
          <div className="flex w-full flex-col items-start justify-start gap-4">
            <div className="flex items-center justify-start gap-2 text-base">
              <Link prefetch={false} href={ROUTES.HOME()} className="text-white">
                {t('text160')}
              </Link>
              <span className="text-white/70">/</span>
              <span className="text-white/70">{service.name}</span>
              {city && (
                <>
                  <span className="text-white/70">/</span>
                  <span className="text-white/70">{city}</span>
                </>
              )}
            </div>
            <h1 className="md:leadinhg-[1.1] text-[28px] font-bold leading-[1.1] text-white sm:text-3xl sm:leading-[1.1] md:text-4xl lg:text-5xl lg:leading-[1.1] xl:text-[56px] xl:leading-[1.1]">
              {service.name} {city && city}
            </h1>
            <div className="flex w-full flex-wrap items-start justify-start gap-2">
              {tags?.map(tag => (
                <ServiceTag tag={tag} key={tag} />
              ))}
            </div>
          </div>
          <div
            onClick={handleScrollDown}
            className="hidden cursor-pointer items-center justify-start gap-2.5 text-base text-white transition-all hover:opacity-80 sm:flex"
          >
            <RxDoubleArrowDown size={24} /> {t('text2404')}
          </div>
        </div>
        <Button asChild variant={'primary-foreground'} className="whitespace-nowrap">
          <Link prefetch={false} href={`/${service.slug}#${id}`}>
            <LuPlus size={24} /> {t('text3523')}
          </Link>
        </Button>
      </div>
      <div className="absolute inset-0 z-[1] h-full w-full bg-black/50" />
      <Image
        src={service?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(service?.image, service?.name)}
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        sizes="100vw"
        priority
        loading="eager"
        fetchPriority="high"
      />
    </section>
  );
}

function ServiceTag({ tag }: { tag: string }) {
  if (tag === '') return null;
  return (
    <div className="flex items-center justify-center gap-2 rounded-full bg-white/30 px-2 py-1.5 text-white sm:px-3 sm:py-2 lg:px-4">
      <Image
        src={'/images/white-check.svg'}
        alt={tag}
        width={0}
        height={0}
        className="max-h-4 min-h-4 min-w-4 max-w-4 sm:max-h-5 sm:min-h-5 sm:min-w-5 sm:max-w-5 lg:max-h-6 lg:min-h-6 lg:min-w-6 lg:max-w-6"
      />
      {tag}
    </div>
  );
}
